// Tenant Contact Queries - React Query hooks for tenant contact search
// Used by ContactRecipientMultiSelect for autocomplete functionality

import { useApiClient } from '@commander/api-client';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { createTenantContactApi, type TenantContactSearchResult } from '../../api/tenantContactApi';

/**
 * Hook to search tenant contacts by keyword
 * Search is enabled when keyword has at least 1 character
 * Results are cached for 30 seconds
 *
 * @param keyword - Search term (name or email)
 * @param limit - Maximum results (default 10)
 * @param masterCompanyLocationId - Optional location ID to scope contacts by location
 * @returns React Query result with TenantContactSearchResult[]
 */
export function useSearchTenantContacts(
  keyword: string,
  limit = 10,
  masterCompanyLocationId?: string
) {
  const apiClient = useApiClient();
  const api = useMemo(() => createTenantContactApi(apiClient), [apiClient]);

  return useQuery<TenantContactSearchResult[]>({
    // Include masterCompanyLocationId in query key for proper cache isolation
    queryKey: ['tenant-contacts', 'search', keyword, limit, masterCompanyLocationId],
    queryFn: () => api.searchTenantContacts(keyword, limit, masterCompanyLocationId),
    enabled: keyword.length >= 1, // Start search after 1 character
    staleTime: 30000, // Cache results for 30 seconds
  });
}
